import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils/hashing"
import {ImageNode} from "#template/nodes/image/image-node"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ColorValue, NumberLike} from "#template/nodes/types"
import {colorValue} from "#template/nodes/validation"
import {numberLike} from "#template/nodes/number/fundamental"
import {NodesInput} from "#template/fundamental/input/nodes-input"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator} from "@cm/material-nodes"
import {color as colorNode, value} from "@cm/material-nodes"

const isColorValue = (v: unknown): v is ColorValue => Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === "number")

export type ImageRgbaParameters = {
    name: string
    color: ColorValue | NodesInput
    alpha: NumberLike
}

const imageRgbaDefinition: TemplateNodeDefinition<ImageRgbaParameters> = {
    nodeClass: "ImageRgba",
    validation: {
        parameters: z.object({
            name: z.string(),
            color: z.union([colorValue, graphNode(NodesInput)]),
            alpha: numberLike,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ImageRgba extends ImageNode<ImageRgbaParameters> {
    public override get definition() {
        return imageRgbaDefinition
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        const {color: colorParam, alpha: alphaParam} = this.parameters

        const evaluatedColorValue = isColorValue(colorParam)
            ? colorParam
            : scope.pureLambda(
                  evaluator.evaluate(colorParam),
                  (evaluatedNodesInput) => {
                      const maybeColorValue = evaluatedNodesInput
                          .filter((evaluatedNode) => evaluatedNode.type === "Number")
                          .map((evaluatedNode) => evaluatedNode.value)
                      if (!isColorValue(maybeColorValue)) return null
                      return maybeColorValue
                  },
                  "colorValue",
              )
        const alphaValue = evaluator.evaluate(alphaParam)

        return scope.pureLambda<[ColorValue | null, number | null], ImageGenerator | null>(
            scope.tuple(evaluatedColorValue, alphaValue),
            ([color, alpha]) => {
                if (color === null || alpha === null) return null

                return {
                    imageNode: {
                        generator: (_generatorData) => ({
                            color: colorNode({
                                r: color[0],
                                g: color[1],
                                b: color[2],
                            }),
                            alpha: value(alpha),
                        }),
                        hash: hashObject({type: "ImageRgba", color, alpha}),
                    },
                }
            },
            "imageRgba",
        )
    }
}
